// Shared logic to rebuild the staff + audio sequence for any saved composition.
// Handles three cases:
//   • remix_events present → exact verbatim events
//   • remix_taps present   → words in tap order
//   • otherwise            → words in input order
//
// Hebrew compositions are detected by character range and use buildHebrewSequence
// with the saved scale_root + scale_mode. English uses buildLetterSequence.
import { buildLetterSequence } from "@/lib/composition";
import { buildHebrewSequence } from "@/lib/hebrewComposition";
import { isHebrewLetter } from "@/lib/hebrewCryptogram";
import { PRESETS } from "@/lib/presets";

function syntheticStepMs(charIdx, wordLen) {
  const p = PRESETS.meditation;
  const isFirstInWord = charIdx === 0 && wordLen > 1;
  const isLastInWord = charIdx === wordLen - 1;
  const isSecondToLast = charIdx === wordLen - 2 && wordLen >= 3;
  let stepMs = p.baseStepMs + p.stepJitterMs * 0.5;
  if (isFirstInWord) stepMs *= p.firstLetterMult;
  if (isSecondToLast) stepMs *= p.secondToLastMult;
  if (isLastInWord) stepMs *= p.lastLetterMult;
  return stepMs;
}

export function isHebrewComposition(comp) {
  return comp.words.some((w) => [...w].some((ch) => isHebrewLetter(ch)));
}

export function buildStaffAndAudio(comp) {
  if (comp.remix_events && comp.remix_events.length > 0) {
    const noteEvents = comp.remix_events.filter((ev) => ev.kind === "note");
    const staffEntries = noteEvents.map((ev, idx) => ({
      note: ev.note,
      accidental: ev.accidental || null,
      octave: ev.octave,
      pitch: ev.pitch,
      letter: ev.letter,
      charIdx: ev.charIdx,
      wordIdx: `event-${idx}`,
      displayWordIdx: ev.displayWordIdx,
      stepMs: ev.stepMs,
    }));
    return { staffEntries, audioEvents: comp.remix_events, mode: "events" };
  }

  const isHebrew = isHebrewComposition(comp);
  const base = isHebrew
    ? buildHebrewSequence(comp.words, {
        root: comp.scale_root,
        modeIdx: comp.scale_mode,
        scale: comp.scale_family,
      })
    : buildLetterSequence(comp.words);
  const wordLens = base.reduce((acc, e) => {
    acc[e.wordIdx] = (acc[e.wordIdx] || 0) + 1;
    return acc;
  }, {});
  const decorated = base.map((e) => ({
    ...e,
    stepMs: syntheticStepMs(e.charIdx, wordLens[e.wordIdx]),
  }));

  if (comp.remix_taps && comp.remix_taps.length > 0) {
    const seq = [];
    comp.remix_taps.forEach((tap, tapIdx) => {
      const w = decorated.filter((e) => e.wordIdx === tap.wordIdx);
      w.forEach((e) =>
        seq.push({ ...e, wordIdx: `tap-${tapIdx}`, displayWordIdx: e.wordIdx })
      );
    });
    return { staffEntries: seq, audioEvents: seq, mode: "sequence" };
  }
  return { staffEntries: decorated, audioEvents: decorated, mode: "sequence" };
}
